import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ICONS } from '@app/styles';
import { _className, Button, LL_H_C, openContent, TS_Card, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { Editor_Term } from '../editors/Editor_Term.js';
import './Component_TermsAndExpression.scss';
import { Editor_TopExpression } from '../editors/top-expression/Editor_TopExpression.js';
import { ActionsMenu_BaseManagerUI, IconWithTooltip } from '@app/styles';
import { TS_ListOrganizer } from '@nu-art/thunderstorm-frontend/components/TS_ListOrganizer/index';
import { CollectLabelSwaps, updateExpressionString } from '../editors/utils.js';
import { ExpressionType_NormalRange, LogicType_Complex, LogicType_StructuredFreeText } from '@app/hcs-shared';
import { ModuleFE_Expression } from '../../../../../../_entity.js';
import { DropDown_SingleAtomLogics } from '../../../../../../_entity/expression/index.js';
import { getEmptyTerm } from '../../../../../../_entity/expression/utils.js';
import { TS_EditableItemComponentProto } from '@nu-art/thunderstorm-frontend/editable-item';
export class Component_TermsAndExpression extends TS_EditableItemComponentProto {
    //######################### Lifecycle #########################
    static defaultProps = {
        usedVarIds: []
    };
    deriveStateFromProps(nextProps, state) {
        state = super.deriveStateFromProps(nextProps, state);
        state.cardWidth ??= 700;
        return state;
    }
    async componentDidMount() {
        window.addEventListener('expression-clipboard-changed', this.handleClipboardEvent);
        const hasCopiedTerm = !!(await ModuleFE_Expression.getCopiedTerm());
        if (this.mounted)
            this.setState({ hasCopiedTerm });
    }
    componentWillUnmount() {
        window.removeEventListener('expression-clipboard-changed', this.handleClipboardEvent);
    }
    //######################### Logic #########################
    addTerm = async (term) => {
        const emptyTerm = {
            expression: 'A1',
            atoms: [{ leftHand: undefined, cases: [{}] }],
        };
        //every new term must be created with at least one empty atom and case in it
        const editableTerms = this.props.editable.editProp('terms', []);
        await editableTerms.updateArrayAt(term ?? emptyTerm);
        this.forceUpdate();
    };
    deleteTerm = async (index) => {
        const editableTerms = this.props.editable.editProp('terms', []);
        await editableTerms.removeArrayItem(index);
        this.forceUpdate();
    };
    hasValidationError = (index) => {
        const editableTerms = this.state.editable.editProp('terms', []);
        const currentTerm = editableTerms.editProp(index, {});
        if (currentTerm.hasError('atomsLogic'))
            return currentTerm.hasError('atomsLogic');
        const termsHasError = this.state.editable.hasError('terms');
        return typeof termsHasError === 'string' ? termsHasError : undefined;
    };
    reorganizeTerms = async (labelSwaps, newOrder) => {
        const stringExpression = updateExpressionString(labelSwaps, this.state.editable.get('expression'));
        await this.state.editable.updateObj({
            expression: stringExpression,
            terms: newOrder.map(item => item.editable.item)
        });
    };
    pasteTerm = async () => {
        const clipboardTerm = await ModuleFE_Expression.getCopiedTerm();
        if (!clipboardTerm)
            return;
        const editableTerm = this.state.editable.editProp('terms', []);
        return editableTerm.updateArrayAt(clipboardTerm.data);
    };
    /**
     * Refresh the paste button state whenever the expression clipboard changes (fired by ModuleFE_Expression copies).
     */
    handleClipboardEvent = async () => {
        const clipboardTerm = await ModuleFE_Expression.getCopiedTerm();
        if (this.mounted)
            this.setState({ hasCopiedTerm: !!clipboardTerm });
    };
    //######################### Render #########################
    renderAddTermButton = () => {
        const item = this.props.editable.item;
        if (this.props.viewMode || (item.expressionType === ExpressionType_NormalRange.dbKey && item.terms?.length))
            return '';
        return _jsxs(LL_H_C, { style: { gap: '10px' }, children: [_jsx(Button, { variant: 'primary', className: 'add-term-btn', onClick: () => this.addTerm(), children: "Add Term" }), _jsx(Button, { variant: 'secondary', disabled: !this.state.hasCopiedTerm, className: 'add-term-btn', onClick: this.pasteTerm, children: "Paste Term" })] });
    };
    renderActionMenu = (index) => {
        if (this.props.viewMode)
            return '';
        const menuActions = [
            {
                label: 'Remove Term',
                action: () => this.deleteTerm(index)
            }
        ];
        if (this.state.editable.item?.terms?.[index])
            menuActions.push({
                label: 'Copy Term To Clipboard',
                action: async () => {
                    await ModuleFE_Expression.copyTermToClipboard(this.state.editable.item.terms[index], index);
                    this.setState({ hasCopiedTerm: true });
                }
            });
        if (this.state.editable.get('expressionType') !== ExpressionType_NormalRange.dbKey)
            menuActions.push({
                label: 'Duplicate',
                action: () => this.addTerm(this.state.editable.item.terms?.[index])
            });
        return _jsx(ICONS.more.component, { ...openContent.popUp.bottom('pop-up__term-actions', () => _jsx(ActionsMenu_BaseManagerUI, { actions: menuActions }), 10) });
    };
    renderTermLabel(index, onDragStart, onDragEnd) {
        const validationErrorMessage = this.hasValidationError(index);
        const editableTerms = this.state.editable.editProp('terms', []);
        const currentTerm = editableTerms.editProp(index, {});
        const currentLogic = currentTerm.get('atomsLogic');
        return _jsxs(_Fragment, { children: [!this.props.hideDnD ? _jsx(ICONS.dragHandle.component, { onDragStart: e => {
                        const container = e.currentTarget.parentNode.parentNode;
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/html', container);
                        e.dataTransfer.setDragImage(container, 30, 30);
                        onDragStart(e, index);
                    }, onDragEnd: onDragEnd, className: 'term-drag-handle', draggable: true }) : null, _jsx("div", { className: 'term-header', children: `Term (T${index + 1})` }), currentTerm.item.atoms?.length === 1 &&
                    _jsx(DropDown_SingleAtomLogics.editable, { editable: currentTerm, prop: 'atomsLogic', canUnselect: true, onSelected: value => {
                            const changes = {
                                atomsLogic: value
                            };
                            // return if there's no change
                            if (currentLogic === value)
                                return;
                            //Handle complex changes
                            if (currentLogic === LogicType_Complex) {
                                changes.fallbackValue = undefined;
                                changes.subTerms = undefined;
                            }
                            if (value === LogicType_Complex) {
                                changes.subTerms = [getEmptyTerm()];
                                changes.fallbackValue = [0, 0];
                            }
                            //Handle free text changes
                            if (currentLogic === LogicType_StructuredFreeText) {
                                changes.structuredFreeText = undefined;
                                changes.outputType = undefined;
                            }
                            if (value === LogicType_StructuredFreeText) {
                                changes.structuredFreeText = { if: undefined, then: undefined, else: undefined };
                            }
                            return currentTerm.updateObj(changes);
                        } }), !!validationErrorMessage && _jsx(IconWithTooltip, { content: validationErrorMessage, iconKey: 'attention' }), this.renderActionMenu(index)] });
    }
    renderTerm = (props) => {
        const className = _className('expression-card', 'term-card', this.props.hideCards && 'hide-card');
        return _jsx(TS_Card, { onDragOver: e => props.onDragOver(e, props.index), onDragLeave: e => props.onDragLeave(e, props.index), className: className, children: _jsx(TS_PropRenderer.Vertical, { label: this.renderTermLabel(props.index, props.onDragStart, props.onDragEnd), children: _jsx(Editor_Term, { usedVarIds: this.props.usedVarIds, editable: props.item.editable, deleteTerm: () => this.deleteTerm(props.index), viewMode: this.props.viewMode, expressionType: this.state.editable.get('expressionType') }) }) }, props.index);
    };
    renderTermsList = () => {
        const editableTerms = this.props.editable.editProp('terms', []);
        const terms = editableTerms.item.map((term, index) => ({
            editable: editableTerms.editProp(index, {}),
            label: `T${index + 1}`
        }));
        return _jsxs(_Fragment, { children: [_jsx(TS_ListOrganizer, { items: terms, renderer: this.renderTerm, onOrderChanged: newTermOrder => this.reorganizeTerms(CollectLabelSwaps(newTermOrder, terms), newTermOrder) }), this.renderAddTermButton()] });
    };
    renderTopExpression = () => {
        return _jsx(Editor_TopExpression, { hideCards: this.props.hideCards, viewMode: !!this.props.viewMode, editable: this.state.editable });
    };
    render() {
        return _jsxs(_Fragment, { children: [this.renderTermsList(), this.renderTopExpression()] });
    }
}
//# sourceMappingURL=Component_TermsAndExpression.js.map